import { useEffect, useRef, useState } from "react";
import { getActiveCountry, formatLocalPrice, RATES_UPDATED, getLocaleOverride, ISO_COUNTRY_META } from "../../lib/locale";
import { LocationCTA } from "./LocationCTA";

// Shared, persuasive plan comparison — the single source of truth for the
// upgrade pitch. Used in BOTH the Operator paywall card and the Settings
// billing panel so the copy is identical everywhere. The copy below is
// psychology-tuned and FROZEN (Authorized unfreeze: renamed turns -> credits).
//
type BillingPricing = {
  currency: string;
  detected: string;
  supported: string[];
  plans: Record<string, { unit_amount: number; currency: string } | null>;
};

// Format a Stripe minor-unit amount. Unlike formatLocalPrice (an FX estimate
// off a hand-maintained rate table) this is the exact figure Stripe will
// charge, so it is rendered without any "approximate" hedging.
function formatExact(unitAmount: number, currency: string): string | null {
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency.toUpperCase(),
      maximumFractionDigits: unitAmount % 100 === 0 ? 0 : 2,
    }).format(unitAmount / 100);
  } catch {
    return null;
  }
}

// "Wall vs panel" is derived from whether currentPlan is passed:
//   • paywall card → no currentPlan → isWall=true  → amber theme + header
//   • settings panel → currentPlan set → isWall=false → neutral theme, no header
// (matches the two call sites; no variant prop needed.)

type CheckoutPlan = "starter" | "pro";
type TierPlan = CheckoutPlan | "business" | "enterprise";
const CUSTOM_CALL_URL = "https://calendly.com/electrofyne/demo-walkthrough";

type Tier = {
  plan: TierPlan;
  name: string;
  price: string;
  tagline: string;
  subtitle: string;
  benefits: string[];
  footer: string;
  recommended?: boolean;
  enterprise?: boolean;
  action: "checkout" | "call" | "current";
  cta: string;
  href?: string;
};

// FROZEN COPY — see header note. (Unfrozen for credit rename).
const TIERS: Tier[] = [
  {
    plan: "starter",
    name: "Starter",
    price: "$49/mo",
    tagline: "Getting started",
    subtitle: "For solo operators and small teams finding their footing.",
    benefits: [
      "400 operator credits a month",
      "Your core Operator + one specialist agent",
      "Connect your data and tools",
      "Send email from your own inbox",
    ],
    footer: "Best for: putting Sloe to work on real tasks before you scale.",
    action: "checkout",
    cta: "Choose Starter",
  },
  {
    plan: "pro",
    name: "Pro",
    price: "$149/mo",
    tagline: "RECOMMENDED",
    subtitle: "For operators that run on Sloe every day.",
    benefits: [
      "2,500 credits a month — effectively unlimited for daily work",
      "Every agent, every connector",
      "Generate documents & send approved emails",
      "Send from your own branded domain",
      "Priority support",
    ],
    footer: "Best for: the operator who runs their work inside Sloe. Less than a day of hired help.",
    recommended: true,
    action: "checkout",
    cta: "Choose Pro",
  },
  {
    plan: "business",
    name: "Custom Call",
    price: "$399/mo",
    tagline: "Custom",
    subtitle: "For teams that need a tailored build and a live scoping call.",
    benefits: [
      "One scheduled call to scope the build",
      "I integrate the app or workflow you need",
      "Custom setup around your team",
      "Best for non-standard requests",
    ],
    footer: "Best for: founders who want a custom build instead of a generic plan.",
    action: "call",
    cta: "Book a Call",
    href: CUSTOM_CALL_URL,
  },
  {
    plan: "enterprise",
    name: "Enterprise",
    price: "Let's talk",
    tagline: "Custom",
    subtitle: "Custom integrations, white-glove setup, dedicated support.",
    benefits: [],
    footer: "For organizations with specific needs. We build around you.",
    enterprise: true,
    action: "call",
    cta: "Let's talk",
    href: CUSTOM_CALL_URL,
  },
];

export default function PlanComparison({
  osId,
  currentPlan,
  onSelect,
}: {
  osId: string;
  currentPlan?: string | null;
  onSelect?: (plan: string) => void;
}) {
  // Render is intentionally ZERO-FETCH: tier data is the static TIERS const, so
  // this component paints instantly with no network wait. The only fetch is
  // /api/billing/checkout, fired on a tier click (startCheckout). If the wall
  // ever feels slow to appear, the latency is upstream (the chat round-trip or
  // the usage check that surfaces the card), never here.
  const isWall = currentPlan === undefined;
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState("");
  const [activeCountry, setActiveCountry] = useState(getActiveCountry());
  // Authoritative per-currency amounts from Stripe (null until they arrive).
  // Deliberately NOT awaited before first paint — see the note above.
  const [billing, setBilling] = useState<BillingPricing | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Fetch the real amounts for the active country's currency. Refetches when
  // the user switches country via LocationCTA. Failure is silent: we simply
  // keep showing the indicative estimate rather than blocking the paywall.
  useEffect(() => {
    let cancelled = false;
    // Only send an explicit currency when the user DELIBERATELY picked a
    // country. getActiveCountry() falls back to browser locale, and
    // navigator.language is "en-US" on most Windows installs worldwide — so
    // using it here made a Canadian visitor request USD and overrode the
    // server's IP-based detection. With no explicit choice we send nothing and
    // let the server decide from Cloudflare's CF-IPCountry, which reflects
    // where the user actually is.
    const overrideIso = getLocaleOverride();
    const overrideCur = overrideIso ? ISO_COUNTRY_META[overrideIso]?.currency : null;
    const cur = overrideCur ? overrideCur.toLowerCase() : "";
    const qs = cur ? `?currency=${encodeURIComponent(cur)}` : "";
    fetch(`/api/billing/pricing${qs}`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => { if (!cancelled && j?.currency) setBilling(j as BillingPricing); })
      .catch(() => { /* keep indicative estimate */ });
    return () => { cancelled = true; };
  }, [activeCountry?.currency]);

  useEffect(() => {
    const handleLocaleChange = () => setActiveCountry(getActiveCountry());
    window.addEventListener("sloe_locale_changed", handleLocaleChange);
    return () => window.removeEventListener("sloe_locale_changed", handleLocaleChange);
  }, []);

  // ── Stuck-button fix ─────────────────────────────────────────────────────
  // Checkout navigates away to Stripe. If the user backs out, the page is
  // restored (often from bfcache) with React state intact, so `busy` would stay
  // set and the buttons stay stuck on "Redirecting…". Reset busy whenever the
  // user comes BACK to the page: pageshow (incl. bfcache restore), the tab
  // becoming visible again, or window focus. Plus an 8s timeout safety net in
  // case the redirect never fires at all.
  useEffect(() => {
    const reset = () => {
      if (timeoutRef.current) { clearTimeout(timeoutRef.current); timeoutRef.current = null; }
      setBusy(null);
    };
    const onPageShow = () => reset();
    const onVisibility = () => { if (!document.hidden) reset(); };
    const onFocus = () => reset();
    window.addEventListener("pageshow", onPageShow);
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", onFocus);
    return () => {
      window.removeEventListener("pageshow", onPageShow);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", onFocus);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  async function startCheckout(plan: CheckoutPlan) {
    setErr("");
    setBusy(plan);
    onSelect?.(plan);
    // Safety net: if we never navigate (error swallowed, popup blocked, etc.),
    // re-enable the buttons after 8s so the UI is never permanently stuck.
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => setBusy(null), 8000);
    try {
      const res = await fetch("/api/billing/checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ os_id: osId, plan, currency: billing?.currency }),
      });
      const j = await res.json().catch(() => ({}));
      if (res.ok && j?.url) { window.location.href = j.url; return; }
      setErr(j?.error || "Could not start checkout.");
    } catch { setErr("Could not start checkout."); }
    if (timeoutRef.current) { clearTimeout(timeoutRef.current); timeoutRef.current = null; }
    setBusy(null);
  }

  // Theme tokens — amber on the wall (matches the paywall card), neutral zinc in
  // Settings. Content/structure are identical; only the palette differs.
  const t = isWall
    ? {
        card: "border-amber-300 bg-white",
        cardRec: "border-amber-500 ring-2 ring-amber-400/60 bg-white",
        name: "text-amber-900",
        price: "text-amber-900",
        tagline: "text-amber-700",
        subtitle: "text-amber-800",
        benefit: "text-amber-900",
        check: "text-amber-500",
        footer: "text-amber-700",
        badge: "bg-amber-500 text-white",
        select: "bg-amber-500 hover:bg-amber-600 text-white",
        selectRec: "bg-amber-500 hover:bg-amber-600 text-white",
        ghost: "border border-amber-300 text-amber-800 hover:bg-amber-100",
        current: "border border-amber-200 text-amber-600 bg-amber-50",
        risk: "text-amber-700",
      }
    : {
        card: "border-zinc-200 bg-white",
        cardRec: "border-zinc-900 ring-2 ring-zinc-900/10 bg-white",
        name: "text-zinc-900",
        price: "text-zinc-900",
        tagline: "text-zinc-500",
        subtitle: "text-zinc-500",
        benefit: "text-zinc-700",
        check: "text-emerald-500",
        footer: "text-zinc-500",
        badge: "bg-zinc-900 text-white",
        select: "bg-zinc-900 hover:bg-zinc-800 text-white",
        selectRec: "bg-zinc-900 hover:bg-zinc-800 text-white",
        ghost: "border border-zinc-200 text-zinc-700 hover:bg-zinc-50",
        current: "border border-zinc-200 text-zinc-400 bg-zinc-50",
        risk: "text-zinc-400",
      };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3 mb-1">
        {isWall ? (
          <div>
            <p className="text-base font-bold text-amber-900">Keep your OS running</p>
            <p className="text-sm text-amber-800 mt-1">
              You've used your free credits. Pick a plan to keep your agents working — or book the $399 custom call if you need a specific app wired in.
            </p>
          </div>
        ) : <div />}
        <LocationCTA />
      </div>

      <div className="space-y-2.5">
        {TIERS.map((tier) => {
          const isCurrent = !tier.enterprise && currentPlan === tier.plan;
          const rec = !!tier.recommended;
          const usdVal = tier.plan === "starter" ? 49 : tier.plan === "pro" ? 149 : tier.plan === "business" ? 399 : 0;
          // Exact Stripe amount wins; the indicative estimate is the fallback
          // for currencies Stripe has no currency_options entry for.
          const exactRow = billing?.plans?.[tier.plan] || null;
          const exactPrice = exactRow ? formatExact(exactRow.unit_amount, exactRow.currency) : null;
          const localPrice = exactPrice || (activeCountry ? formatLocalPrice(usdVal, activeCountry.currency) : null);
          return (
            <div
              key={tier.plan}
              className={`relative rounded-xl border px-4 py-3.5 ${rec ? t.cardRec : t.card}`}
            >
              {rec ? (
                <span className={`absolute -top-2 left-4 px-2 py-0.5 rounded-full text-[10px] font-bold tracking-wide ${t.badge}`}>
                  RECOMMENDED
                </span>
              ) : null}
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className={`text-sm font-bold ${t.name}`}>{tier.name}</span>
                    <div className="inline-flex flex-col">
                      <span className={`text-sm font-semibold ${t.price}`}>
                        {localPrice ? `${localPrice}/mo` : tier.price}
                      </span>
                    </div>
                    {!rec ? (
                      <span className={`text-[11px] uppercase tracking-wide ${t.tagline}`}>{tier.tagline}</span>
                    ) : null}
                  </div>
                  <p className={`text-xs mt-0.5 ${t.subtitle}`}>{tier.subtitle}</p>
                </div>
              </div>

              {tier.benefits.length > 0 ? (
                <ul className="mt-2.5 space-y-1">
                  {tier.benefits.map((b, k) => (
                    <li key={k} className={`flex gap-2 text-xs ${t.benefit}`}>
                      <span className={`shrink-0 ${t.check}`}>✓</span>
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              ) : null}

              <p className={`text-[11px] mt-2.5 ${t.footer}`}>{tier.footer}</p>

              <div className="mt-3">
                {tier.action === "call" ? (
                  <a
                    href={tier.href || CUSTOM_CALL_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`inline-flex items-center justify-center w-full px-4 py-2 rounded-lg text-sm font-medium transition-colors ${t.ghost}`}
                  >
                    {tier.cta}
                  </a>
                ) : isCurrent ? (
                  <span
                    className={`inline-flex items-center justify-center w-full px-4 py-2 rounded-lg text-sm font-medium ${t.current}`}
                  >
                    Current plan
                  </span>
                ) : (
                  <button
                    type="button"
                    disabled={!!busy}
                    onClick={() => startCheckout(tier.plan as CheckoutPlan)}
                    className={`inline-flex items-center justify-center w-full px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-60 ${rec ? t.selectRec : t.select}`}
                  >
                    {busy === tier.plan ? "Redirecting…" : tier.cta}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {err ? <p className="text-xs text-red-600">{err}</p> : null}

      {billing?.plans?.starter ? (
        billing.currency !== "usd" ? (
          <p className={`text-[11px] text-center ${t.risk}`}>
            Billed in {billing.currency.toUpperCase()}. This is the exact amount you'll be charged.
          </p>
        ) : null
      ) : activeCountry && formatLocalPrice(49, activeCountry.currency) ? (
        <p className={`text-[11px] text-center ${t.risk}`}>
          Billed in USD. Local amount approximate (rates updated {RATES_UPDATED}).
        </p>
      ) : null}

      <p className={`text-[11px] text-center ${t.risk}`}>
        Cancel anytime · No setup fees · Secure checkout via Stripe
      </p>
    </div>
  );
}
